import { useContext, useEffect } from "react";
import { AuthContext } from "../components/CognitoAuthProvider";
import { getSafeRedirectUrl } from "../utils/redirectUtils";

// Module-level token getter for non-React contexts (e.g., apiClient)
let _tokenGetter = null;

/**
 * Get an access token for API calls.
 * Used by apiClient to attach Bearer tokens to requests.
 */
export async function getAccessToken() {
  if (!_tokenGetter) {
    console.debug('Auth token getter not initialized');
    return null;
  }
  return _tokenGetter();
}

/**
 * Provider component to initialize auth token access for services.
 * Place inside CognitoAuthProvider so context is available.
 */
export const AuthTokenProvider = ({ children }) => {
  useAuth(); // triggers token getter registration
  return children;
};

/**
 * Custom hook to provide authentication utilities.
 * Abstracts the underlying auth provider (Cognito) so components
 * don't need to know the implementation details.
 * 
 * Usage: const { isAuthenticated, loginWithRedirect, logout, user, isLoading } = useAuth();
 */
export function useAuth() {
  const auth = useContext(AuthContext);

  if (!auth) {
    throw new Error('useAuth must be used within a CognitoAuthProvider');
  }

  // Auto-register token getter for non-React contexts (apiClient)
  useEffect(() => {
    _tokenGetter = auth.getAccessToken;
  }, [auth.getAccessToken]);

  // Compatibility: loginWithRedirect navigates to /login
  const loginWithRedirect = (options = {}) => {
    const returnTo = getSafeRedirectUrl(options?.appState?.redirect_uri || window.location.pathname);
    window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  };

  // Compatibility: signupWithRedirect navigates to /signup
  const signupWithRedirect = (options = {}) => {
    const returnTo = getSafeRedirectUrl(options?.appState?.redirect_uri || window.location.pathname);
    window.location.href = `/signup?returnTo=${encodeURIComponent(returnTo)}`;
  };

  // Compatibility: logout wraps signOut
  const logout = async (options = {}) => {
    await auth.signOut();
    const returnTo = getSafeRedirectUrl(options?.logoutParams?.returnTo || options?.returnTo);
    window.location.href = returnTo;
  };

  return {
    // State
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    user: auth.user,
    isAdmin: auth.user?.groups?.includes('admin'),

    // Navigation-based actions (compatibility with old Auth0 API)
    loginWithRedirect,
    signupWithRedirect,
    logout,

    // Direct Cognito actions (for auth pages)
    signIn: auth.signIn,
    signInPasswordless: auth.signInPasswordless,
    confirmSignIn: auth.confirmSignIn,
    signUp: auth.signUp,
    confirmSignUp: auth.confirmSignUp,
    autoSignIn: auth.autoSignIn,
    resendSignUpCode: auth.resendSignUpCode,
    signOut: auth.signOut,
    forgotPassword: auth.forgotPassword,
    confirmForgotPassword: auth.confirmForgotPassword,

    // Token access
    getAccessTokenSilently: auth.getAccessToken,
    getIdTokenClaims: auth.getIdToken,
  };
}
